/**
 * The builder's assistant (ai-plan-builder R2, R3, R13 to R19; board S): whether Buddy is offered on
 * this plan, which conversation it is, the plan a message carries, and where a proposal is applied.
 *
 * ── Which conversation ───────────────────────────────────────────────────────────────────────
 * A saved library plan has its conversation by its id (`planTemplateId`, R13); an unsaved plan
 * Buddy built goes on in the conversation it came from (`origin: aiDraft`); any other unsaved plan is
 * keyed by the identity its device copy keeps (`useUnsavedPlanDocumentKey`), so a reload of the
 * same plan comes back to the same conversation. A plan assigned to a client is out of Buddy's reach
 * (R2), and outside the beta there is no assistant at all (R3).
 *
 * ── The plan travels with every message ──────────────────────────────────────────────────────
 * The snapshot is the plan in the save request's shape, unfinished parts kept
 * (`draftToSnapshotRequest`), with its version: the fingerprint of that whole request (`snapshotVersion`), not the store's revision.
 * The revision restarts at every load and counts undo steps, so two different trees can carry the
 * same number across a reload; the fingerprint names the content itself, the same on any page that
 * holds the same tree, and a decision names the version its proposal was made on.
 */

import { createElement } from 'react';

import {
  draftToSnapshotRequest,
  readPlanDraft,
  snapshotVersion,
  useIsForkPlan,
  useIsUnsavedNewPlan,
  usePlanAiConversationId,
  usePlanDraftId,
  usePlanRecipient,
  useUnsavedPlanDocumentKey,
} from '~/entities/plan-draft';
import { useBuddyEntry, type BuddyEntry } from '~/entities/ai-conversation';
import { useBuddyConversation, type BuddyConversation, type BuddyConversationSource, type DraftForMessage, type ProposalHost } from '~/features/ai-chat';

import { ProposalExerciseChange } from '../ui/ProposalExerciseChange';

export interface PlanAssistant {
  /** Buddy's standing for this trainer: offered, or switched off for now (R3, R44). */
  readonly entry: Exclude<BuddyEntry, 'hidden'>;
  /** The conversation, read only while the panel is open: a closed panel starts nothing. */
  readonly conversation: BuddyConversation;
  /** The plan as a message carries it now. */
  readonly draftForMessage: () => DraftForMessage;
  readonly proposalHost: ProposalHost;
}

/** The builder's own plan: the store holds it, so a proposal stands against the store's tree. */
const BUILDER_HOST: ProposalHost = {
  where: 'builder',
  draftOf: (_draftTurnId, storeTree) => storeTree,
  hold: () => readPlanDraft() !== null,
  renderExerciseChange: (change) => createElement(ProposalExerciseChange, { change }),
};

function draftForMessage(): DraftForMessage {
  const tree = readPlanDraft();
  if (tree === null) return { kind: 'none' };
  // An unfinished plan travels as it is (owner, 5 October 2026): only a save refuses one.
  const plan = draftToSnapshotRequest(tree);
  return { kind: 'ready', snapshot: { plan, version: snapshotVersion({ ...plan }) } };
}

/** `null` when Buddy does not work on this plan: a client's plan (R2), or one not loaded yet. */
function sourceOf(
  planId: number | null,
  conversationId: number | null,
  documentKey: string | null,
  clientId: number | null,
): BuddyConversationSource | null {
  if (planId !== null) return { kind: 'plan', planTemplateId: planId };
  if (conversationId !== null) return { kind: 'id', conversationId };
  if (documentKey !== null) return { kind: 'key', draftKey: documentKey, clientId };
  return null;
}

/**
 * The assistant on this plan, or `null` where there is none (outside the beta, a client's plan, no
 * plan loaded). `open`: the panel stands, so the conversation is read (and created when it has none).
 */
export function usePlanAssistant(userId: string, open: boolean): PlanAssistant | null {
  const entry = useBuddyEntry(userId);
  const fork = useIsForkPlan();
  const unsaved = useIsUnsavedNewPlan();
  const planId = usePlanDraftId();
  const conversationId = usePlanAiConversationId();
  const documentKey = useUnsavedPlanDocumentKey();
  const recipient = usePlanRecipient();
  // An unsaved plan that is no longer unsaved work was assigned: it is the client's now (R2).
  const assigned = planId === null && !unsaved;
  const source = fork || assigned ? null : sourceOf(planId, conversationId, documentKey, recipient?.clientId ?? null);
  // Called whatever the plan is, so the hook order never changes; a key of `null` reads and creates nothing.
  const conversation = useBuddyConversation(open && source !== null ? source : { kind: 'key', draftKey: null, clientId: null }, userId);
  if (source === null || entry === 'hidden') return null;
  return { entry, conversation, draftForMessage, proposalHost: BUILDER_HOST };
}
